/**
 * LoadingView Component - Estado de Loading da Tela de Resultados
 * @module features/results/components/LoadingView
 *
 * Componente que exibe o estado de loading enquanto o relatório
 * está sendo verificado ou gerado.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 *
 * Features:
 * - Spinner animado
 * - Skeleton elements para preview do conteúdo
 * - Mensagem customizável
 * - Acessibilidade completa (aria-busy, aria-live)
 */

import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { LoadingViewProps } from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Mensagem padrão exibida durante loading
 */
const DEFAULT_MESSAGE = 'Preparando seu relatório...';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Componente de loading para tela de resultados
 *
 * @param props - Props do componente
 * @param props.message - Mensagem customizada (opcional)
 *
 * @example
 * ```tsx
 * <LoadingView message="Gerando diagnóstico personalizado..." />
 * ```
 */
export function LoadingView({ message = DEFAULT_MESSAGE }: LoadingViewProps) {
  return (
    <div
      data-testid="loading-view"
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn('flex items-center justify-center', 'min-h-[400px] p-8', 'flex-col gap-8')}
    >
      {/* Spinner */}
      <Loader2
        data-testid="loading-spinner"
        className={cn('h-16 w-16 animate-spin', 'text-primary')}
      />

      {/* Message */}
      <div className="text-center space-y-2">
        <p className="text-lg font-medium text-foreground">{message}</p>
        <p className="text-sm text-muted-foreground">Isso pode levar alguns segundos.</p>
      </div>

      {/* Screen reader text */}
      <span className="sr-only">Por favor, aguarde enquanto preparamos seu relatório.</span>

      {/* Skeleton elements */}
      <div className="w-full max-w-md space-y-4">
        {/* Title skeleton */}
        <div data-testid="loading-skeleton" className="h-8 bg-muted rounded animate-pulse" />
        <div data-testid="loading-skeleton" className="h-4 bg-muted rounded animate-pulse w-3/4" />
        <div data-testid="loading-skeleton" className="h-4 bg-muted rounded animate-pulse w-1/2" />

        {/* Card skeleton (AssessmentPreview) */}
        <div
          data-testid="loading-skeleton-card"
          className={cn('h-32 bg-muted rounded-lg animate-pulse', 'border border-border')}
        />

        {/* Scores skeleton (DimensionScores) */}
        <div
          data-testid="loading-skeleton-scores"
          className={cn(
            'bg-muted rounded-lg animate-pulse',
            'border border-border',
            'p-4 space-y-3'
          )}
        >
          {/* Overall score skeleton */}
          <div className="flex justify-between items-center pb-3 border-b border-border/50">
            <div className="h-4 bg-muted-foreground/20 rounded w-24 animate-pulse" />
            <div className="flex gap-2 items-center">
              <div className="h-6 bg-muted-foreground/20 rounded w-10 animate-pulse" />
              <div className="flex gap-1">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-3 w-3 bg-muted-foreground/20 rounded-full animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
          {/* Dimension items skeleton */}
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex justify-between items-center py-1">
              <div className="h-4 bg-muted-foreground/20 rounded w-20 animate-pulse" />
              <div className="flex gap-1">
                {[...Array(6)].map((_, j) => (
                  <div
                    key={j}
                    className="h-2 w-2 bg-muted-foreground/20 rounded-full animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Insight skeleton (InsightQuote) */}
        <div
          data-testid="loading-skeleton-insight"
          className={cn('bg-muted rounded-lg animate-pulse', 'border border-border', 'p-4 pl-8')}
        >
          <div className="h-4 bg-muted-foreground/20 rounded w-full animate-pulse mb-2" />
          <div className="h-4 bg-muted-foreground/20 rounded w-3/4 animate-pulse" />
        </div>

        {/* Button skeletons */}
        <div className="flex flex-col gap-3">
          <div
            data-testid="loading-skeleton-button"
            className="h-12 bg-muted rounded-lg animate-pulse"
          />
          <div
            data-testid="loading-skeleton-button"
            className="h-10 bg-muted rounded-lg animate-pulse"
          />
        </div>
      </div>
    </div>
  );
}

export default LoadingView;
